import React, { useState, memo } from "react";
import type { Attachment } from "@/types/chat";
import { useChatInput } from "@/hooks/useChatInput";
import { useChatContext } from "@/components/chatview/ChatContext";
import { useUserStore } from "@/store/UserStore";
import { useChatIdStore } from "@/store/ChatIdStore";
import ToastEvent from "@/utils/toast-event";
import { isImageFile } from "@/utils/imageFile";
import { FileInput } from "./FileInput";
import { FilePreviewDialog } from "./FilePreviewDialog";
import { InputArea } from "./InputArea";
import { InputSuggest } from "./InputSuggest";

interface ChatBottomProps {
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  stop: () => void;
  attachments: Attachment[];
  setAttachments: (
    attachments: Attachment[] | ((prev: Attachment[]) => Attachment[])
  ) => void;
  hasMessages: boolean;
  append: (message: {
    content: string;
    role: "user" | "assistant";
  }) => Promise<void>;
  handleSubmit: (e: React.FormEvent) => void;
}

export const ChatBottom = memo(function ChatBottom({
  input,
  setInput,
  isLoading,
  stop,
  attachments,
  setAttachments,
  hasMessages,
  append,
  handleSubmit,
}: ChatBottomProps) {
  const { activeQuote, setActiveQuote } = useChatContext();
  const { setStartChatId } = useChatIdStore();
  const {
    textareaRef,
    fileInputRef,
    handleInput,
    submitForm,
    handleFileChange,
    uploadQueue,
  } = useChatInput({
    input,
    setInput,
    isLoading,
    stop,
    attachments,
    setAttachments,
    handleSubmit,
  });

  // 预览弹窗状态
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<any>(null);
  const [previewType, setPreviewType] = useState<"image" | "text" | "other">(
    "image"
  );

  // 处理文件预览
  const handleFilePreview = (
    attachment: any,
    fileUrl: string | null,
    fileName: string
  ) => {
    if (!fileUrl) return;

    const fileExtension = fileName.split(".").pop()?.toLowerCase();
    const isImageFileType = isImageFile(
      fileName,
      attachment.contentType || attachment.type,
    );
    const isTextFile = ["txt", "md", "json"].includes(fileExtension || "");

    setPreviewItem({
      ...attachment,
      url: fileUrl,
      name: fileName,
    });

    if (isImageFileType) {
      setPreviewType("image");
    } else if (isTextFile && attachment.originalFile instanceof File) {
      setPreviewType("text");
    } else {
      setPreviewType("other");
    }

    setIsPreviewOpen(true);
  };

  function checkLoginStatus() {
    const token = useUserStore.getState().token;
    return !!token;
  }

  const handleRemoveAttachment = (id: string) => {
    setAttachments(attachments.filter((attach) => attach.id !== id));
  };

  const onSubmit = () => {
    if (!checkLoginStatus()) {
      ToastEvent.error("请先登录再使用本产品");
      return;
    }

    if (activeQuote && activeQuote.trim()) {
      submitForm({ quote: activeQuote });
      setActiveQuote?.(null);
      setStartChatId(0);
    } else {
      submitForm();
      setStartChatId(0);
    }
  };

  const handleShortcutClick = async (message: string) => {
    if (!checkLoginStatus()) {
      ToastEvent.error("请先登录再使用本产品");
      return;
    }

    await append({ content: message, role: "user" });
  };

  return (
    <>
      <div className="relative w-full flex flex-col bg-[var(--color-background)]">
        {hasMessages && (
          <InputSuggest onSendMessage={handleShortcutClick} />
        )}

        <FileInput ref={fileInputRef} onChange={handleFileChange} multiple />

        <FilePreviewDialog
          isOpen={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          previewItem={previewItem}
          previewType={previewType}
        />

        <InputArea
          input={input}
          setInput={setInput}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onStop={stop}
          onFileClick={() => fileInputRef.current?.click()}
          onFileChange={handleFileChange}
          disabled={
            (input.length === 0 &&
              attachments.filter((attach) => attach.url).length === 0) ||
            uploadQueue.length > 0
          }
          textareaRef={textareaRef}
          handleInput={handleInput}
          attachments={attachments}
          onRemoveAttachment={handleRemoveAttachment}
          onFilePreview={handleFilePreview}
          connectedTop={hasMessages}
        />
      </div>
    </>
  );
});
